import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Pencil, Save } from 'lucide-react'

import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { FormControl, FormField, FormItem, FormLabel } from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'
import { Loader } from '@/shared/ui/loader'
import { Textarea } from '@/shared/ui/textarea'
import { Client } from '@monorepo/functions/src/types/client'

import { useClient } from '../../../../../entities/client/model/useClient'

interface ClientSummaryProps {
    clientId: string
}

type ClientFormData = Pick<Client, 'name' | 'lastName' | 'phone' | 'email' | 'address' | 'internalNotes'>

export function ClientSummary({ clientId }: ClientSummaryProps) {
    const { client, setClientSync: setClient } = useClient(clientId)
    const [isEditing, setIsEditing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const methods = useForm<ClientFormData>({
        defaultValues: client,
    })

    if (!client) return <Loader />

    const handleEditToggle = () => {
        if (!isEditing) {
            methods.reset(client)
            setIsEditing(true)
        }
    }

    const onSubmit = async (data: ClientFormData) => {
        try {
            setIsSaving(true)
            await setClient({ ...client, ...data })
            setIsEditing(false)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Card>
            <CardHeader className='flex flex-row items-center justify-between'>
                <CardTitle>Información del Cliente</CardTitle>
                {!isEditing && (
                    <Button variant='outline' onClick={handleEditToggle} className='!mt-0'>
                        <Pencil className='mr-2 h-4 w-4' />
                        Editar Información
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                {isEditing ? (
                    <div>
                        <FormProvider {...methods}>
                            <form onSubmit={methods.handleSubmit(onSubmit)} className='space-y-4'>
                                <FormField
                                    control={methods.control}
                                    name='name'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Nombre</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={methods.control}
                                    name='lastName'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Apellidos</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={methods.control}
                                    name='phone'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Teléfono</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={methods.control}
                                    name='email'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Email</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={methods.control}
                                    name='address'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Dirección</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={methods.control}
                                    name='internalNotes'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Notas Internas</FormLabel>
                                            <FormControl>
                                                <Textarea {...field} value={field.value || ''} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <div className='flex justify-end gap-2'>
                                    <Button variant='outline' type='button' onClick={() => setIsEditing(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type='submit' disabled={isSaving}>
                                        {isSaving ? (
                                            <Loader />
                                        ) : (
                                            <>
                                                <Save className='mr-2 h-4 w-4' />
                                                Guardar Cambios
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </FormProvider>
                    </div>
                ) : (
                    <div className='grid gap-4'>
                        <div>
                            <label className='mb-1 block text-sm font-medium text-gray-700'>Nombre</label>
                            <p>{client.name}</p>
                        </div>
                        <div>
                            <label className='mb-1 block text-sm font-medium text-gray-700'>Apellidos</label>
                            <p>{client.lastName}</p>
                        </div>
                        <div>
                            <label className='mb-1 block text-sm font-medium text-gray-700'>Teléfono</label>
                            <p>{client.phone}</p>
                        </div>
                        <div>
                            <label className='mb-1 block text-sm font-medium text-gray-700'>Email</label>
                            <p>{client.email}</p>
                        </div>
                        <div>
                            <label className='mb-1 block text-sm font-medium text-gray-700'>Dirección</label>
                            <p>{client.address}</p>
                        </div>
                        <div>
                            <label className='mb-1 block text-sm font-medium text-gray-700'>Notas Internas</label>
                            <p>{client.internalNotes || 'Sin notas internas'}</p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
